import { forwardRef, useEffect, useMemo, useState } from 'react';
import {
    getTabsSizeClasses,
    getTabsStyleClasses,
    TabsSize,
    TTabsProps,
    NovoIconNames,
    TabsSizeType,
} from '@novonordisk/novoui-vuejs';
import { NIcon, NTooltip } from '@innersource-nn/novoui-react';
import { ExternalIconNames } from '@innersource-nn/novoui-react/dist/external-lib/NIcon/icons';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { NTabsContents } from '@innersource-nn/novoui-react/dist/external-lib/NTabs/NTabsProps';
import { useAlert } from 'components/message/AlertContext';

interface ExtendedTabsContents extends NTabsContents {
    tooltip?: string;
}

interface NTabsProps extends Omit<TTabsProps, 'content'> {
    content?: ExtendedTabsContents[];
}

interface TabButtonProps {
    tab: ExtendedTabsContents;
}
interface ExtendedNTabsProps
    extends NTabsProps,
        Omit<
            React.ButtonHTMLAttributes<HTMLTableElement>,
            'content' | 'children'
        > {}

const defaultNTabsProps: NTabsProps = {
    size: TabsSize.md,
    content: [],
    contentClasses: [],
    height: undefined,
    sticky: false,
};

interface TabsIconProps {
    icon: NovoIconNames | ExternalIconNames;
    size?: TabsSizeType;
    label: string;
}
const TabIcon = ({ icon, size, label }: TabsIconProps) => {
    return (
        <NIcon
            iconName={icon}
            size={
                size === TabsSize.large ||
                (size === TabsSize.lg && !(label && label.trim()))
                    ? 'normal'
                    : 'small'
            }
        />
    );
};

/**
 * TabsPanel component is a wrapper around the Novo React library's Tabs component, we are only extending the funcionality to be able to use the tooltips in some of the tabs
 **/
export const TabsPanel = forwardRef<HTMLDivElement, ExtendedNTabsProps>(
    (
        {
            // activeTabId = defaultNTabsProps.activeTabId,
            size = defaultNTabsProps.size,
            content = defaultNTabsProps.content,
            contentClasses = defaultNTabsProps.contentClasses,
            height = defaultNTabsProps.height,
            sticky = defaultNTabsProps.sticky,
            className,
            ...restProps
        },
        ref,
    ) => {
        const location = useLocation();
        const navigate = useNavigate();
        const { openAlert } = useAlert();
        const searchParams = createSearchParams(location.search).get('tab');

        // Update the URL with the tabId everytime a tab is clicked
        const switchTo = (tabId: string) => {
            navigate(
                {
                    pathname: location.pathname,
                    search: `tab=${tabId}`,
                },
                { replace: true },
            );
            setCurrentTabId(tabId);
        };

        // Imported functions from the Novo React library
        const tabsSize = useMemo(() => getTabsSizeClasses({ size }), []);

        const tabsStyleClasses = useMemo(
            () => getTabsStyleClasses({ sticky }),
            [],
        );

        const [currentTabId, setCurrentTabId] = useState(
            (content && content[0]?.id) || '0',
        );

        useEffect(() => {
            if (searchParams !== null) {
                const currentTab = content?.find(
                    (tab) => tab.id === searchParams,
                );
                if (currentTab?.disabled) {
                    openAlert({
                        message: currentTab?.tooltip,
                        title: `${currentTab.label}  tab is blocked`,
                        severity: 'alert',
                    });
                    navigate(
                        {
                            pathname: location.pathname,
                            search: '',
                        },
                        { replace: true },
                    );
                } else setCurrentTabId(searchParams);
            }
        }, [searchParams]);

        function TabButton({ tab }: TabButtonProps) {
            return (
                <li key={tab.id}>
                    <button
                        onClick={() => switchTo(tab.id)}
                        className={`${tabsSize} ${currentTabId === tab.id ? 'tab-active' : ''}`}
                        disabled={tab.disabled}
                    >
                        {tab.icon && (
                            <TabIcon
                                icon={tab.icon}
                                size={size}
                                label={tab.label}
                            />
                        )}
                        {tab.disabled && (
                            <TabIcon
                                icon="lock"
                                size={size}
                                label={tab.label}
                            />
                        )}
                        {tab.label && tab.label.trim() && (
                            <span>{tab.label}</span>
                        )}
                    </button>
                </li>
            );
        }

        return (
            <div
                className={`tabs-container ${className || ''} mt-16`}
                ref={ref}
                {...restProps}
            >
                <ul className={`bg-true-blue rounded-t-12 ${tabsStyleClasses}`}>
                    {content?.map((tab) =>
                        tab.tooltip ? (
                            <NTooltip
                                content={tab.tooltip ?? ''}
                                variant="light"
                                placement="down"
                                fixed={true}
                                key={'tooltip-' + tab.id}
                            >
                                <TabButton tab={tab} />
                            </NTooltip>
                        ) : (
                            <TabButton tab={tab} />
                        ),
                    )}
                </ul>
                <div
                    className={`tabs-content bg-white rounded-b-12 ${contentClasses}`}
                    style={{
                        height: `${height}px`,
                        overflowY: height ? 'auto' : 'initial',
                    }}
                >
                    {content?.map(
                        (tab) =>
                            tab.id === currentTabId && (
                                <div key={tab.id}>
                                    {/* Dynamic slot created for each content element corresponding to a tab. Access via `tabs_${tabID}`; `tabID` comes from the `content` prop */}
                                    {tab.content}
                                </div>
                            ),
                    )}
                </div>
            </div>
        );
    },
);
